// Plans Actions

import get from 'lodash-es/get';
import AvailabilityApiClient from '../../../api/clients/AvailabilityApiClient';
import RoomBookingApiClient from '../../../api/clients/RoomBookingApiClient';
import { isBBAR } from '../../../api/Utils/BestAvailableRate';
import { GwDatesWrapper } from '../../../components/_internal_date_/gwDatesWrapper';
import { rateCalendarGA } from '../../../infrastructure/middleware/analytics/analyticsObjectBuilders';
import RecentSearchesRepository from '../../../repositories/RecentSearchesRepository';
import { getTestValueAB, TESTS_DICTIONARY } from '../../../utilities/ABTesting';
import {
  EVENT_CATEGORIES,
  trackVariationHEventError,
  trackVariationJEventError
} from '../../../utilities/applicationInsights';
import { getTheFirstElementOfArray } from '../../../utilities/arraysUtils';
import { getNormalizedCode } from '../../../utilities/availabilityUtils';
import { DATE_FORMATS, GTM_EVENT } from '../../../utilities/constants';
import { pushEvent } from '../../../utilities/dataLayerUtils';
import { isDateValid } from '../../../utilities/dates';
import { getNumberOfBillableGuests } from '../../../utilities/guestsUtils';
import { getResortLocation } from '../CurrentLodge/currentLodge.selectors';
import { gaTrackingPlanSearchErrorCode } from '../utilities/util';
import plansActionTypes from './plans.types';

const transformRestrictions = data => {
  const dates = Object.keys(data);
  return { restrictions: dates.reduce((items, current) => ({ ...items, [current]: data[current].restrictions }), {}) };
};

export const getLowestRatesByStayNoSuiteCode = requestObject => async dispatch => {
  //If is less than 1 or more than 30 days the API will not run
  //======================================================================================
  if (
    GwDatesWrapper.diff(requestObject.departure, requestObject.arrival, 'days') < 1 ||
    GwDatesWrapper.diff(requestObject.departure, requestObject.arrival, 'days') > 30
  )
    return;

  const apiClient = new RoomBookingApiClient('v1.0');

  try {
    const lowestRatesByStayResponse = await apiClient.getLowestRatesForSuiteByStayNoSuiteCode(requestObject);
    const { daily } = lowestRatesByStayResponse?.data;
    dispatch({
      type: plansActionTypes.OPEN_PLANS_CALENDAR_RATES_BY_STAY,
      lowestRatesByStayList: daily
    });
    dispatch({
      type: plansActionTypes.LAST_PLANS_CALENDAR_RATES_BY_STAY_SELECTION,
      lastCalendarRatesDateSelection: {
        start: requestObject.arrival,
        end: requestObject.departure
      }
    });
  } catch (err) {
    console.log(err);
  }
};

export const cleanLowestRatesByStayNoSuiteCode = () => dispatch => {
  dispatch({
    type: plansActionTypes.CLEAN_PLANS_CALENDAR_RATES_BY_STAY
  });
};

const getLowestRatesByStay = async (
  shouldFetchRatesByStay,
  requestObject,
  suiteCode,
  lowestRatesResponse,
  dispatch
) => {
  const apiClient = new RoomBookingApiClient('v1.0');
  if (!shouldFetchRatesByStay) {
    dispatch({
      type: plansActionTypes.OPEN_PLANS_CALENDAR_RATES_BY_STAY,
      lowestRatesByStayList: lowestRatesResponse
    });
    return lowestRatesResponse;
  }

  try {
    const lowestRatesByStayResponse = await apiClient.getLowestRatesForSuiteByStay(
      requestObject,
      suiteCode,
      requestObject.lodgeCode,
      requestObject.offerCode
    );
    const returnData = { ...lowestRatesResponse, ...lowestRatesByStayResponse?.data?.daily };
    dispatch({
      type: plansActionTypes.OPEN_PLANS_CALENDAR_RATES_BY_STAY,
      lowestRatesByStayList: returnData
    });
    return returnData;
  } catch (err) {
    return {};
  }
};

const getCalendarRates = async (requestObject, suiteCode, dispatch) => {
  const apiClient = new RoomBookingApiClient('v1.0');
  const lowestRatesResponse = await apiClient.getLowestRatesForSuite(requestObject, suiteCode, requestObject.lodgeCode);
  const existingLowestRates = lowestRatesResponse?.data;
  return getByStayCalendarRates(requestObject, suiteCode, dispatch, existingLowestRates);
};

const getByStayCalendarRates = async (requestObject, suiteCode, dispatch, existingLowestRates) => {
  const hasArrivalInTheMonth = existingLowestRates?.[requestObject.arrival];
  const hasDepartureInTheMonth = existingLowestRates?.[requestObject.departure];
  const shouldFetchRatesByStay = hasArrivalInTheMonth || hasDepartureInTheMonth;
  return await getLowestRatesByStay(shouldFetchRatesByStay, requestObject, suiteCode, existingLowestRates, dispatch);
};

export const setIsErrorModalAlreadyClosed = value => dispatch => {
  dispatch({
    type: plansActionTypes.SET_PLANS_IS_ERROR_MODAL_ALREADY_CLOSED,
    value: value
  });
};
export const setIsCartOpenClose = value => dispatch => {
  dispatch({
    type: plansActionTypes.SET_PLANS_IS_CART_OPEN_CLOSE,
    value
  });
};
export const setIsCartOpenCloseClean = () => dispatch => {
  dispatch({
    type: plansActionTypes.SET_PLANS_IS_CART_OPEN_CLOSE_CLEAN
  });
};
export const setCartReturnedPageLinkClicked = pageToReturn => dispatch => {
  dispatch({
    type: plansActionTypes.SET_PLANS_CART_RETURNED_PAGE_LINK_CLICKED,
    pageToReturn: pageToReturn
  });
};
export const setCartReturnedPageLinkClickedClean = () => dispatch => {
  dispatch({
    type: plansActionTypes.SET_PLANS_CART_RETURNED_PAGE_LINK_CLICKED_CLEAN
  });
};
// Perform the request search by Phone and Cc to API endpoint
export const getAvailability = (requestObject, shouldIgnoreOfferCodeError = false) => (dispatch, getState) => {
  dispatch({
    type: plansActionTypes.GET_PLANS_AVAILABILITY_INFO_LOADING,
    shouldIgnoreOfferCodeError: shouldIgnoreOfferCodeError,
    availabilityReqIsLoading: true,
    offerCodeError: undefined,
    availabilityList: []
  });

  // This way is the one we are going to use for get the Authorization from the AuthClient
  // Call General Availability endpoint to retrieve info on available suites
  let apiClient = new AvailabilityApiClient('v2.3');

  const request = apiClient.fetchAvailability(requestObject);

  return request.then(
    response => {
      const existsValidSuite = response && response.data && response.data.suites && response.data.suites.length > 0;
      const errorCode = get(response, 'data.errorCode', undefined);
      const promoDescription = get(response, 'data.planDescription', '');
      const rateCode = existsValidSuite && response.data.suites[0].rateCode;
      const isQualifyIdRequired = get(response, 'data.isQualifyIdRequired', false);
      const isIdMeValidationRequired = get(response, 'data.isIdMeValidationRequired', false);
      const hasErrorCode = errorCode && requestObject.offerCode && isBBAR(rateCode);
      const personalizationRules = response.data.pr;
      const personalizationResponses = get(response, 'data.rr', []);
      const offerType = get(response, 'data.offerType', '');
      const lcoPackages = get(response, 'data.lco', []);
      const upsellPackagesContent = get(response, 'data.upsellPackagesContent', []);
      const availabilityList = existsValidSuite ? response.data.suites : [{}];
      const isTwoRateTypesSuites =
        existsValidSuite && response?.data?.suites?.some(suite => suite.rateOptions?.length > 1);

      //This code below until the end tag is for create a tracking for version J and H issues
      //======================================================================================
      const optimizelyDisplayResortFeeWithTotalInSuiteCardJ = getTestValueAB(
        TESTS_DICTIONARY.optimizelyDisplayResortFeeWithTotalInSuiteCardJ,
        false
      );
      const optimizelyDisplayResortFeeWithTotalInSuiteCardH = getTestValueAB(
        TESTS_DICTIONARY.optimizelyDisplayResortFeeWithTotalInSuiteCardH,
        false
      );
      const { latestVersionJEventErrorObj, latestVersionHEventErrorObj } = getState().plans;
      const { resortLocation } = getState().currentLodge;
      const { pricePerWaterPass } = getState().lodgeConfig;
      const { locationList } = getState().lodgeLocations;
      const { checkinDateSelection, checkoutDateSelection } = getState().dates;
      const { adultsCount, kidsCount, kidsAges } = getState().guests;
      const { code } = getState().offer;
      const lodgeLocationName = locationList[resortLocation]?.url;
      //Start version H tracking code
      //=============================
      if (optimizelyDisplayResortFeeWithTotalInSuiteCardH) {
        const numberOfBillableGuests = getNumberOfBillableGuests(adultsCount, kidsAges);
        const numberOfNights = GwDatesWrapper.diff(checkoutDateSelection, checkinDateSelection, 'days');
        //Savings percentage calculation START
        //=========================================
        let savingsLessThanFiveUpperToFithty = [];
        availabilityList?.forEach(suite => {
          const validGuests = numberOfBillableGuests <= 4 ? 4 : numberOfBillableGuests;
          const suiteOnlyRate = suite?.rateOptions?.find(rate => rate.rateType === 'ROOM_ONLY')?.averageNightlyBaseRate;
          const suiteWithWaterParkRate = suite?.rateOptions?.find(rate => rate.rateType === 'WITH_WP')
            ?.averageNightlyBaseRate;
          const DPP_total = pricePerWaterPass * validGuests * (numberOfNights + 1);
          const DPP_new = (suiteWithWaterParkRate - suiteOnlyRate) * numberOfNights;
          const savings = DPP_total - DPP_new;
          const percentage = ((savings / DPP_total) * 100).toFixed();
          if (percentage < 5 || percentage > 50) {
            savingsLessThanFiveUpperToFithty.push({
              suiteName: suite.title,
              percentage: percentage + '%'
            });
          }
        });
        //Savings percentage calculation END
        //===================================
        if (savingsLessThanFiveUpperToFithty.length > 0) {
          const variationHObj = {
            lodge: lodgeLocationName,
            dates:
              'Arrival:' +
              GwDatesWrapper.format(checkinDateSelection, DATE_FORMATS.default) +
              ' Departure:' +
              GwDatesWrapper.format(checkoutDateSelection, DATE_FORMATS.default),
            promoCode: code,
            partySize: adultsCount + kidsCount,
            suiteStyle: 'version H',
            savings: savingsLessThanFiveUpperToFithty
          };
          if (JSON.stringify(latestVersionHEventErrorObj) !== JSON.stringify(variationHObj)) {
            trackVariationHEventError(EVENT_CATEGORIES.OnVersionHEventError, variationHObj);
            dispatch({
              type: plansActionTypes.SET_PLANS_LATEST_H_VERSION_EVENT_ERROR,
              variationHObj
            });
          }
        }
      }
      //End version H tracking code
      //=============================
      //Start version J tracking code
      //===============================
      if (optimizelyDisplayResortFeeWithTotalInSuiteCardJ) {
        const isJVersionAndIsTwoRatesTypesPresent =
          optimizelyDisplayResortFeeWithTotalInSuiteCardJ && isTwoRateTypesSuites;
        const isJVersionAndIsCaliforniaResident = optimizelyDisplayResortFeeWithTotalInSuiteCardJ;
        const isJVersionAndIsTwoRatesTypesPresentAndNonTwoRatesPresent =
          isJVersionAndIsTwoRatesTypesPresent &&
          availabilityList?.some(
            suite => suite.availabilityKey && (suite.rateOptions === null || suite.rateOptions?.length <= 1)
          );
        if (
          availabilityList?.length > 0 &&
          (!isJVersionAndIsTwoRatesTypesPresent ||
            isJVersionAndIsCaliforniaResident ||
            isJVersionAndIsTwoRatesTypesPresentAndNonTwoRatesPresent)
        ) {
          const variationJObj = {
            lodge: lodgeLocationName,
            dates:
              'Arrival:' +
              GwDatesWrapper.format(checkinDateSelection, DATE_FORMATS.default) +
              ' Departure:' +
              GwDatesWrapper.format(checkoutDateSelection, DATE_FORMATS.default),
            promoCode: code,
            partySize: adultsCount + kidsCount,
            rateCodes: availabilityList
              ?.filter(
                suite =>
                  suite.availabilityKey !== null && (suite.rateOptions?.length <= 1 || suite.rateOptions === null)
              )
              .map(suite => {
                return {
                  name: suite.title,
                  rateCode: suite.rateCode
                };
              }),
            suiteStyle: isJVersionAndIsTwoRatesTypesPresentAndNonTwoRatesPresent ? 'version J and default' : 'default'
          };
          if (JSON.stringify(latestVersionJEventErrorObj) !== JSON.stringify(variationJObj)) {
            trackVariationJEventError(EVENT_CATEGORIES.OnVersionJEventError, variationJObj);
            dispatch({
              type: plansActionTypes.SET_PLANS_LATEST_J_VERSION_EVENT_ERROR,
              variationJObj
            });
          }
        }
      }
      //End version J tracking code
      //=============================

      // RECOMMENDATIONS AB TEST
      const optimizelyReturnZSRecommendation = getTestValueAB(TESTS_DICTIONARY.optimizelyReturnZSRecommendation, false);
      if (optimizelyReturnZSRecommendation) {
        const roomBookingApiClient = new RoomBookingApiClient('v1.0');
        const addonsRequest = roomBookingApiClient.getAddOnsRecommendations(resortLocation, requestObject);

        addonsRequest.then(response => {
          dispatch({
            type: plansActionTypes.SET_PLANS_UPSELL_RECOMMENDATION_DATA,
            upsellRecommendationData: response.data
          });
        });
        addonsRequest.catch(err => {
          console.log('err', err);
        });
      }
      // END OF RECOMMENDATIONS AB TEST

      dispatch({
        type: plansActionTypes.GET_PLANS_AVAILABILITY_INFO_SUCCESS,
        availabilityList: availabilityList,
        isAPScall: existsValidSuite ? response.data.suites.some(st => st.availabilityKey) : false,
        isTwoRateTypesSuites: isTwoRateTypesSuites,
        availabilityReqIsLoading: false,
        requiresQualifyingID: isQualifyIdRequired,
        isIdMeValidationRequired: isIdMeValidationRequired,
        isIdMeValidationRequiredWithError: false,
        isErrorModalAlreadyClosed: false,
        promoDescription: promoDescription,
        personalizationRules: personalizationRules,
        personalizationResponses: personalizationResponses,
        offerCodeError: hasErrorCode
          ? {
              errorCode: errorCode,
              offerCode: getNormalizedCode(requestObject.offerCode)
            }
          : undefined,
        offerType: offerType,
        lcoPackages: lcoPackages,
        upsellPackagesContent: upsellPackagesContent,
        errors: response.data.errors
      });
      if (hasErrorCode)
        gaTrackingPlanSearchErrorCode(
          JSON.stringify({
            errorCode: errorCode,
            offerCode: getNormalizedCode(requestObject.offerCode)
          })
        );
    },
    err => {
      const errorCode = err.err?.response?.data?.Error?.ErrorCode;
      //ID.me treatment
      //=========================================================
      if (errorCode === 'IDME001') {
        //Verify if it's ID.me case
        //=================================
        const getElement = setInterval(() => {
          const idMeElement = document.getElementById('idme-wallet-button');
          if (idMeElement !== undefined) {
            if (window.BindIDme) {
              window.BindIDme();
            }
            clearInterval(getElement);
          }
        }, 1500);
      }
      dispatch({
        type: plansActionTypes.GET_PLANS_AVAILABILITY_INFO_ERROR,
        shouldIgnoreOfferCodeError: shouldIgnoreOfferCodeError,
        availabilityList: [],
        promoDescription: '',
        requiresQualifyingID: false,
        isIdMeValidationRequired: errorCode === 'IDME001' ? true : false,
        isIdMeValidationRequiredWithError: true,
        availabilityReqIsLoading: false,
        availabilityErrorMessage: `Oops, ${typeof err === 'string' ? err : err.message}`,
        offerCodeError: undefined
      });
      gaTrackingPlanSearchErrorCode(JSON.stringify(err));
    }
  );
};
export const getAvailabilityHighestRates = requestObject => (dispatch, getState) => {
  const apiClient = new RoomBookingApiClient();

  apiClient
    .fetchAvailabilityHighestRates(requestObject, getResortLocation(getState().currentLodge))
    .then(response => {
      dispatch({
        type: plansActionTypes.GET_PLANS_AVAILABILITY_HIGHEST_RATES_SUCCESS,
        availabilityHighestRatesList: response.data
      });
    })
    .catch(err => {
      const hasDatesExceededError = err?.err?.response?.data?.errors?.['DateRangeExceeded'];

      if (hasDatesExceededError) {
        dispatch({
          type: plansActionTypes.GET_PLANS_AVAILABILITY_HIGHEST_RATES_DATES_EXCEEDED_ERROR,
          availabilityHighestRatesList: []
        });
        return;
      }
      dispatch({
        type: plansActionTypes.GET_PLANS_AVAILABILITY_HIGHEST_RATES_ERROR,
        availabilityHighestRatesList: []
      });
    });
};
export const getRecentSearches = () => dispatch => {
  dispatch({
    type: plansActionTypes.GET_PLANS_RECENT_SEARCHES_INFO_LOADING,
    searchesGetReqIsLoading: true
  });

  const recentSearchesRepository = new RecentSearchesRepository();
  const request = recentSearchesRepository.getAllRecentSearches();

  return request.then(
    response => {
      dispatch({
        type: plansActionTypes.GET_PLANS_RECENT_SEARCHES_INFO_SUCCESS,
        recentSearchesList: response
      });
      dispatch({
        type: plansActionTypes.GET_PLANS_RECENT_SEARCHES_INFO_LOADING,
        searchesGetReqIsLoading: false
      });
    },
    err => {
      dispatch({
        type: plansActionTypes.GET_PLANS_RECENT_SEARCHES_INFO_ERROR,
        recentSearchesList: []
      });
      dispatch({
        type: plansActionTypes.GET_PLANS_RECENT_SEARCHES_INFO_LOADING,
        searchesGetReqIsLoading: false
      });
    }
  );
};
export const addRecentSearch = (recentSearchesList, availabilityData) => dispatch => {
  let recentSearchData = {
    checkin: availabilityData.arrival,
    checkout: availabilityData.departure,
    num_adults: availabilityData.numberOfAdults,
    num_children: availabilityData.numOfBillableChildren,
    num_children2: availabilityData.numOfUnbillableChildren,
    property: availabilityData.location,
    suite: availabilityData.suite ? availabilityData.suite : '',
    offer_code: getNormalizedCode(availabilityData.offerCode),
    kids_ages: availabilityData.kidsAges
  };

  const recentSearchesRepository = new RecentSearchesRepository();
  const request = recentSearchesRepository.addNewRecentSearch(recentSearchesList, recentSearchData);

  return request.then(
    response => {
      dispatch({
        type: plansActionTypes.GET_PLANS_RECENT_SEARCHES_INFO_SUCCESS,
        recentSearchesList: response
      });
    },
    err => {
      console.log(err);
    }
  );
};
export const deleteRecentSearch = (recentSearchesList, recentSearchId) => dispatch => {
  dispatch({
    type: plansActionTypes.DEL_PLANS_REQUEST_RECENT_SEARCHES_INFO_SUCCESS,
    searchesDelReqIsLoading: true
  });

  const recentSearchesRepository = new RecentSearchesRepository();
  const request = recentSearchesRepository.deleteRecentSearchById(recentSearchesList, recentSearchId);

  return request.then(
    response => {
      dispatch({
        type: plansActionTypes.GET_PLANS_RECENT_SEARCHES_INFO_SUCCESS,
        recentSearchesList: response
      });
      dispatch({
        type: plansActionTypes.DEL_PLANS_REQUEST_RECENT_SEARCHES_INFO_SUCCESS,
        searchesDelReqIsLoading: false
      });
    },
    err => {
      dispatch({
        type: plansActionTypes.GET_PLANS_RECENT_SEARCHES_INFO_ERROR,
        recentSearchesList: []
      });
      dispatch({
        type: plansActionTypes.DEL_PLANS_REQUEST_RECENT_SEARCHES_INFO_SUCCESS,
        searchesDelReqIsLoading: false
      });
    }
  );
};
export const deleteAllRecentSearches = () => dispatch => {
  dispatch({
    type: plansActionTypes.DEL_PLANS_REQUEST_RECENT_SEARCHES_INFO_SUCCESS,
    searchesDelReqIsLoading: true
  });

  const recentSearchesRepository = new RecentSearchesRepository();
  const request = recentSearchesRepository.deleteRecentSearchesAll();

  return request.then(
    response => {
      dispatch({
        type: plansActionTypes.GET_PLANS_RECENT_SEARCHES_INFO_SUCCESS,
        recentSearchesList: response
      });
      dispatch({
        type: plansActionTypes.DEL_PLANS_REQUEST_RECENT_SEARCHES_INFO_SUCCESS,
        searchesDelReqIsLoading: false
      });
    },
    err => {
      dispatch({
        type: plansActionTypes.GET_PLANS_RECENT_SEARCHES_INFO_ERROR,
        recentSearchesList: []
      });
      dispatch({
        type: plansActionTypes.DEL_PLANS_REQUEST_RECENT_SEARCHES_INFO_SUCCESS,
        searchesDelReqIsLoading: false
      });
    }
  );
};

export const openRatesCalendar = (suiteCode, requestObject, isFirstTime) => async dispatch => {
  const eventData = rateCalendarGA(GTM_EVENT.ACTION.openRateCalendar, GTM_EVENT.LABEL.greatWolf);

  pushEvent(eventData);
  // Reset the range by suite variables
  if (isFirstTime)
    dispatch({
      type: plansActionTypes.GET_PLANS_AVAILABILITY_RANGE_BY_SUITE_RESET
    });
  dispatch({
    type: plansActionTypes.SELECT_PLANS_SUITE_FOR_RANGE_TYPE,
    selectedSuiteForRange: suiteCode
  });
  dispatch({
    type: plansActionTypes.GET_PLANS_AVAILABILITY_RANGE_BY_SUITE_LOADING,
    getAvRangeBySuiteIsLoading: true
  });

  try {
    const calendarRates = await getCalendarRates(requestObject, suiteCode, dispatch);
    const restrictions = transformRestrictions(calendarRates)?.restrictions;
    dispatch({
      type: plansActionTypes.OPEN_PLANS_RATES_CALENDAR,
      selectedSuiteRange: calendarRates
    });

    dispatch({
      type:
        requestObject?.type === 1
          ? plansActionTypes.OPEN_PLANS_RATES_CALENDAR_RESTRICTION_INCREMENT
          : plansActionTypes.OPEN_PLANS_RATES_CALENDAR_RESTRICTION,
      selectedSuiteRangeRestrictions: restrictions
    });

    dispatch({
      type: plansActionTypes.GET_PLANS_AVAILABILITY_RANGE_BY_SUITE_LOADING,
      getAvRangeBySuiteIsLoading: false
    });
  } catch (err) {
    dispatch({
      type: plansActionTypes.GET_PLANS_AVAILABILITY_RANGE_BY_SUITE_ERROR,
      getAvRangeBySuiteFailed: true
    });
    dispatch({
      type: plansActionTypes.GET_PLANS_AVAILABILITY_RANGE_BY_SUITE_LOADING,
      getAvRangeBySuiteIsLoading: false
    });
  }
};

export const getDataForSelectionInRateCalendar = (suiteCode, requestObject) => async (dispatch, getState) => {
  const selectedSuiteRange = getState()?.plans?.selectedSuiteRange;
  const selectedCalendarRates = await getByStayCalendarRates(requestObject, suiteCode, dispatch, selectedSuiteRange);
  dispatch({
    type: plansActionTypes.OPEN_PLANS_RATES_CALENDAR,
    selectedSuiteRange: { ...selectedSuiteRange, ...selectedCalendarRates }
  });
};
export const closeRatesCalendar = () => {
  const eventData = rateCalendarGA(GTM_EVENT.ACTION.closeRateCalendar, GTM_EVENT.LABEL.greatWolf);
  pushEvent(eventData);
  return {
    type: plansActionTypes.CLOSE_PLANS_RATES_CALENDAR
  };
};
export const getCalendarRangeRestrictions = (requestObject, restrictionsEnabled) => (dispatch, getState) => {
  const resortLocation = getResortLocation(getState().currentLodge);
  if (restrictionsEnabled && !restrictionsEnabled) {
    dispatch({
      type: plansActionTypes.GET_PLANS_AVAILABILITY_RANGE_BY_SUITE_LOADING,
      getAvRangeBySuiteIsLoading: false
    });
    return;
  }

  if (!requestObject.propertyCode) {
    requestObject = {
      ...requestObject,
      propertyCode: resortLocation
    };
  }

  let newRequest = {
    lodgeCode: requestObject?.lodgeCode || requestObject.location,
    year: requestObject.year || requestObject.arrival?.split('-')[0],
    month: requestObject.month || requestObject.arrival?.split('-')[1],
    adults: requestObject.adults || requestObject.numberOfAdults,
    kidsAges: requestObject?.kidsAges,
    offerCode: requestObject.rateCode || requestObject.offerCode || '',
    numberOfMonths: requestObject.numberOfMonths ? requestObject.numberOfMonths : 1
  };

  if (requestObject.type) newRequest.type = requestObject.type;

  let apiClient = new RoomBookingApiClient();
  const request = apiClient.getLowestRates(newRequest, requestObject.propertyCode);

  return request.then(
    async response => {
      const dealsSummary = response.data.dealsSummary && getTheFirstElementOfArray(response.data.dealsSummary);
      const restrictions = transformRestrictions(response.data)?.restrictions;
      //If you have a promocode call API to get the average price
      //===========================================================
      if (
        (requestObject.rateCode || requestObject.offerCode) &&
        +GwDatesWrapper.format(requestObject.arrival, 'MM') === +requestObject.month &&
        requestObject.arrival &&
        isDateValid(requestObject.arrival) &&
        requestObject.departure &&
        isDateValid(requestObject.departure) &&
        GwDatesWrapper.diff(requestObject.departure, requestObject.arrival, 'days') > 1 &&
        GwDatesWrapper.diff(requestObject.departure, requestObject.arrival, 'days') < 30
      ) {
        let lowestRatesByStayRequest = {
          kidsAges: requestObject?.kidsAges,
          adults: requestObject.adults || requestObject.numberOfAdults,
          arrival: requestObject.arrival,
          departure: requestObject.departure,
          lodgeCode: requestObject?.lodgeCode || requestObject.location,
          offerCode: requestObject.rateCode || requestObject.offerCode || ''
        };
        try {
          const lowestRatesByStayResponse = await apiClient.getLowestRatesForSuiteByStayNoSuiteCode(
            lowestRatesByStayRequest
          );
          const { daily } = lowestRatesByStayResponse?.data;
          dispatch({
            type: plansActionTypes.OPEN_PLANS_CALENDAR_RATES_BY_STAY,
            lowestRatesByStayList: daily
          });
          dispatch({
            type: plansActionTypes.LAST_PLANS_CALENDAR_RATES_BY_STAY_SELECTION,
            lastCalendarRatesDateSelection: {
              start: requestObject.arrival,
              end: requestObject.departure
            }
          });
        } catch (err) {
          console.log(err);
        }
      }

      if (newRequest.offerCode !== '') {
        dispatch({
          type:
            requestObject.type === 1
              ? plansActionTypes.GET_PLANS_LOWEST_RATES_CALENDAR_MOBILE
              : plansActionTypes.GET_PLANS_LOWEST_RATES_CALENDAR,
          lowestRateCalendarList: response.data
        });
      }

      dispatch({
        type:
          requestObject?.type === 1
            ? plansActionTypes.OPEN_PLANS_RATES_CALENDAR_RESTRICTION_INCREMENT
            : plansActionTypes.OPEN_PLANS_RATES_CALENDAR_RESTRICTION,
        selectedSuiteRangeRestrictions: restrictions,
        dealsSummary: dealsSummary
      });

      dispatch({
        type: plansActionTypes.GET_PLANS_AVAILABILITY_RANGE_BY_SUITE_LOADING,
        getAvRangeBySuiteIsLoading: false
      });
    },
    () => {
      dispatch({
        type: plansActionTypes.GET_PLANS_AVAILABILITY_RANGE_BY_SUITE_ERROR,
        getAvRangeBySuiteFailed: true
      });
      dispatch({
        type: plansActionTypes.GET_PLANS_AVAILABILITY_RANGE_BY_SUITE_LOADING,
        getAvRangeBySuiteIsLoading: false
      });
    }
  );
};
export const cleanCalendarRestrictionsAndLowestRates = () => {
  return {
    type: plansActionTypes.CLEAN_PLANS_CALENDAR_RESTRICTIONS_AND_LOWEST_RATES
  };
};
export const openPaymentLeaveModal = () => {
  return {
    type: plansActionTypes.OPEN_PLANS_PAYMENT_LEAVE_MODAL
  };
};
export const closePaymentLeaveModal = () => {
  return {
    type: plansActionTypes.CLOSE_PLANS_PAYMENT_LEAVE_MODAL
  };
};
export const setAppliedFilters = params => dispatch => {
  dispatch({
    type: plansActionTypes.SET_PLANS_APPLIED_FILTERS,
    filtersApplied: params
  });
};
export const setSelectedSuiteForCalendar = params => dispatch => {
  dispatch({
    type: plansActionTypes.SET_PLANS_SELECTED_SUITE_NAME_FOR_CALENDAR,
    selectedSuiteForCalendar: params
  });
};

/**
 * @function
 * Sets and toggle when a campaign is being performed on the application
 * @param {bool} isEmailCampaign Whenever if the current url refers to a
 * campaign
 * @param {Object} campaignObject The campaign information
 * @param {date} startDate Start date of the campaign
 * @param {date} endDate End date of the campaign
 */
export const toggleEmailCampaigns = (
  isEmailCampaign,
  campaignObject = {},
  startDate = '',
  endDate = ''
) => dispatch => {
  dispatch({
    type: plansActionTypes.TOGGLE_PLANS_EMAIL_CAMPAIGNS,
    emailCampaign: {
      isEmailCampaign: isEmailCampaign,
      planCalendarShouldBeBlocked: isEmailCampaign,
      startDate: startDate,
      endDate: endDate,
      campaignObject: campaignObject
    }
  });
};

/**
 * @function
 * Clear/reset all the availability data to the initial state   *
 */
export const clearAllAvaliabilityData = () => ({ type: plansActionTypes.CLEAR_PLANS_ALL_AVAILABILITY_DATA });
export const setQualifyingID = code => {
  return {
    type: plansActionTypes.SET_PLANS_QUALIFYING_ID,
    code: code
  };
};
export const getQualifyingID = (shouldTriggerValidation = false) => (dispatch, getState) => {
  const state = getState();
  const { requiresQualifyingID, qualifyingID } = state.plans;
  if (!requiresQualifyingID) return;

  if (!qualifyingID && shouldTriggerValidation) {
    dispatch({
      type: plansActionTypes.GET_PLANS_QUALIFYING_ID_HAS_ERROR,
      hasError: true
    });
  }
  return qualifyingID && qualifyingID.toUpperCase();
};

export const setIsBookingWidgetFocused = (value = false) => dispatch => {
  dispatch({
    type: plansActionTypes.SET_PLANS_IS_BOOKING_WIDGET_FOCUSED,
    value: value
  });
};

export const setSortingOrder = sortingType => ({
  type: plansActionTypes.SET_PLANS_SORTING_ORDER,
  sortingType
});
export const setSortSuite = suiteCode => ({
  type: plansActionTypes.SET_PLANS_SUITE_CODE_FILTER,
  suiteCode
});
